/**
 * Final Inspection Reports Page
 * Quality Management — list QMS reports and WO FIR requests; open full-page detail for view/approve/uploads/print.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  message,
  Typography,
  Row,
  Col,
  Statistic,
  Badge,
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  DownloadOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { finalInspectionReportService } from '@/services/final-inspection-report.service';
import type { FinalInspectionReport } from '@/services/final-inspection-report.service';
import { firRequestService } from '@/services/qc-workflow.service';
import type { FinalInspectionRequest } from '@/services/qc-workflow.service';

function resolveWorkOrderNumber(r: FinalInspectionRequest): string {
  const wo = r.workOrder as
    | { work_order_number?: string; workOrderNumber?: string }
    | undefined;
  return (
    String(wo?.work_order_number || wo?.workOrderNumber || '').trim() ||
    String((r as { work_order_number?: string }).work_order_number || '').trim()
  );
}

/** TypeORM per-line FIR (from WO completion) shown alongside Knex QMS reports. */
function mapFirRequestToReportRow(r: FinalInspectionRequest): FinalInspectionReport {
  const insp = r.inspector as { first_name?: string; last_name?: string; name?: string } | undefined;
  const inspectorName =
    [insp?.first_name, insp?.last_name].filter(Boolean).join(' ').trim() ||
    insp?.name ||
    (r as { inspector_name?: string }).inspector_name ||
    '—';
  const woNum = resolveWorkOrderNumber(r);
  const li = r.workOrderLineItem;
  const itemLabel = li?.item_description
    ? `${li.line_number != null ? `Line ${li.line_number}: ` : ''}${li.item_description}`
    : r.work_order_line_item_id
      ? 'Work order line item'
      : 'Whole work order (legacy)';
  const s = String(r.status ?? '');
  const approval_status: FinalInspectionReport['approval_status'] =
    s === 'Approved' || s === 'Partial'
      ? 'Approved'
      : s === 'Rejected'
        ? 'Rejected'
        : 'Pending Approval';
  const insDate = String(r.inspection_date || r.requested_date || r.created_at);
  const approvedQty = Number(r.approved_quantity ?? 0);
  const rejectedQty = Number(r.rejected_quantity ?? 0);
  const sumQty = approvedQty + rejectedQty;
  const totalQty = sumQty > 0 ? sumQty : Math.max(approvedQty, rejectedQty, 1);
  let statusCol: FinalInspectionReport['status'] = 'Pending';
  if (s === 'Partial') statusCol = 'Partial';
  else if (s === 'In Progress') statusCol = 'In Progress';
  else if (s === 'Rejected') statusCol = 'Rejected';
  else if (s === 'Approved') statusCol = 'Approved';
  else if (s === 'Pending') statusCol = 'Pending';

  return {
    id: r.id,
    fir_number: r.fir_number,
    work_order_id: r.work_order_id,
    work_order_number: woNum,
    item_name: itemLabel,
    inspection_date: insDate,
    inspector_name: inspectorName,
    status: statusCol,
    approved_quantity: approvedQty,
    rejected_quantity: rejectedQty,
    total_quantity: totalQty,
    approval_status,
    approved_by: r.approved_by,
    approved_at: r.approved_date ? String(r.approved_date) : undefined,
    rejection_reason: r.rejection_reason,
    created_at: String(r.created_at),
    quality_parameters: undefined,
    attachments: undefined,
    _recordSource: 'fir_request',
  };
}

const { Title, Text } = Typography;
const { Option } = Select;

export const FinalInspectionReportsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [approvalFilter, setApprovalFilter] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data: reports = [], isLoading } = useQuery({
    queryKey: ['final-inspection-reports', statusFilter, approvalFilter],
    queryFn: async () => {
      const filters: Record<string, string> = {};
      // Status/approval filters stay client-side so WO FIR + QMS rows share one search path.
      // (QMS and FIR request status vocabularies differ; server-side status would drop WO matches.)
      if (approvalFilter !== 'all') filters.approval_status = approvalFilter;

      const [knexReports, firRequests] = await Promise.all([
        finalInspectionReportService.getAll(Object.keys(filters).length ? filters : undefined),
        firRequestService.getAll(),
      ]);
      const fromRequests = (firRequests as FinalInspectionRequest[]).map(mapFirRequestToReportRow);
      const fromKnex = knexReports.map((row) => ({
        ...row,
        work_order_number: String(row.work_order_number || '').trim(),
        _recordSource: 'qms_report' as const,
      }));
      const merged = [...fromRequests, ...fromKnex].sort(
        (a, b) => new Date(b.inspection_date).getTime() - new Date(a.inspection_date).getTime()
      );
      return merged;
    },
  });

  useEffect(() => {
    setPage(1);
  }, [searchText, statusFilter, approvalFilter]);

  const goDetail = (record: FinalInspectionReport) => {
    if (!record._recordSource) {
      message.error('Record source missing; cannot open detail.');
      return;
    }
    queryClient.prefetchQuery({
      queryKey: ['final-inspection-detail', record.id, record._recordSource],
      queryFn: async () => {
        if (record._recordSource === 'qms_report') {
          return { source: 'qms_report' as const, report: await finalInspectionReportService.getById(record.id) };
        }
        return { source: 'fir_request' as const, fir: await firRequestService.getById(record.id) };
      },
    });
    navigate(`/qms/final-inspection-reports/${record.id}?source=${record._recordSource}`);
  };

  const columns: ColumnsType<FinalInspectionReport> = [
    {
      title: 'Record',
      key: '_recordSource',
      width: 110,
      render: (_: unknown, record: FinalInspectionReport) => (
        <Tag color={record._recordSource === 'fir_request' ? 'geekblue' : 'default'}>
          {record._recordSource === 'fir_request' ? 'WO FIR' : 'QMS'}
        </Tag>
      ),
    },
    {
      title: 'FIR Number',
      dataIndex: 'fir_number',
      key: 'fir_number',
      fixed: 'left',
      width: 150,
      render: (text: string, record: FinalInspectionReport) => (
        <Button type="link" onClick={() => goDetail(record)}>
          {text}
        </Button>
      ),
    },
    {
      title: 'Work Order',
      dataIndex: 'work_order_number',
      key: 'work_order_number',
      width: 150,
    },
    {
      title: 'Item',
      dataIndex: 'item_name',
      key: 'item_name',
      width: 200,
    },
    {
      title: 'Inspection Date',
      dataIndex: 'inspection_date',
      key: 'inspection_date',
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Inspector',
      dataIndex: 'inspector_name',
      key: 'inspector_name',
      width: 150,
    },
    {
      title: 'Approved Qty',
      dataIndex: 'approved_quantity',
      key: 'approved_quantity',
      width: 120,
      align: 'right',
      render: (qty: number) => (
        <Text strong style={{ color: '#52c41a' }}>
          {Number(qty || 0).toFixed(2)}
        </Text>
      ),
    },
    {
      title: 'Rejected Qty',
      dataIndex: 'rejected_quantity',
      key: 'rejected_quantity',
      width: 120,
      align: 'right',
      render: (qty: number) => (
        <Text type="danger">{Number(qty || 0).toFixed(2)}</Text>
      ),
    },
    {
      title: 'Inspection Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: string) => {
        const colors: Record<string, string> = {
          Pending: 'orange',
          'In Progress': 'processing',
          Approved: 'green',
          Rejected: 'red',
          Partial: 'gold',
          'On Hold': 'blue',
        };
        return <Tag color={colors[status] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: 'Approval Status',
      dataIndex: 'approval_status',
      key: 'approval_status',
      width: 160,
      render: (status: string) => {
        const colors: Record<string, string> = {
          'Pending Approval': 'warning',
          Approved: 'success',
          Rejected: 'error',
        };
        return <Badge status={colors[status] as any} text={status} />;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 200,
      render: (_: any, record: FinalInspectionReport) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => goDetail(record)}>
            View
          </Button>
          {record.approval_status === 'Pending Approval' &&
            (record._recordSource !== 'fir_request' || record.status === 'In Progress') && (
            <Button
              size="small"
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => goDetail(record)}
            >
              Approve
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const filteredReports = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    const qCompact = q.replace(/[\s_-]/g, '');

    return reports.filter((report) => {
      const fir = String(report.fir_number || '').toLowerCase();
      const wo = String(report.work_order_number || '').toLowerCase();
      const woCompact = wo.replace(/[\s_-]/g, '');
      const item = String(report.item_name || '').toLowerCase();
      const inspector = String(report.inspector_name || '').toLowerCase();

      const matchesSearch =
        !q ||
        fir.includes(q) ||
        wo.includes(q) ||
        (qCompact.length >= 2 && woCompact.includes(qCompact)) ||
        item.includes(q) ||
        inspector.includes(q);

      const matchesStatus =
        statusFilter === 'all' ||
        report.status === statusFilter ||
        (statusFilter === 'Pending' && report.status === 'In Progress');

      const matchesApproval =
        approvalFilter === 'all' || report.approval_status === approvalFilter;

      return matchesSearch && matchesStatus && matchesApproval;
    });
  }, [reports, searchText, statusFilter, approvalFilter]);

  const pendingCount = reports.filter((r) => r.approval_status === 'Pending Approval').length;
  const approvedCount = reports.filter((r) => r.approval_status === 'Approved').length;

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2}>📋 Final Inspection Reports</Title>
            <Text type="secondary">
              QMS final inspection documents (QMS) and per–work order line FIR requests (WO FIR). Use View or Open
              detail for the full page (approve, attachments, print).
            </Text>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12} xl={6}>
            <Card>
              <Statistic
                title="Total Reports"
                value={reports.length}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Card>
              <Statistic
                title="Pending Approval"
                value={pendingCount}
                valueStyle={{ color: '#faad14' }}
                prefix={<CloseCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Card>
              <Statistic
                title="Approved"
                value={approvedCount}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Card>
              <Statistic
                title="Rejection Rate"
                value={
                  reports.length > 0
                    ? ((reports.filter((r) => r.approval_status === 'Rejected').length /
                        reports.length) *
                        100
                      ).toFixed(1)
                    : 0
                }
                suffix="%"
                valueStyle={{
                  color:
                    reports.filter((r) => r.approval_status === 'Rejected').length > 0
                      ? '#ff4d4f'
                      : '#52c41a',
                }}
              />
            </Card>
          </Col>
        </Row>

        <Card>
          <Space wrap>
            <Input
              placeholder="Search FIR, WO, or Item..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
              allowClear
            />
            <Select
              placeholder="Inspection Status"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 180 }}
            >
              <Option value="all">All Status</Option>
              <Option value="Pending">Pending</Option>
              <Option value="In Progress">In Progress</Option>
              <Option value="Approved">Approved</Option>
              <Option value="Partial">Partial</Option>
              <Option value="Rejected">Rejected</Option>
              <Option value="On Hold">On Hold</Option>
            </Select>
            <Select
              placeholder="Approval Status"
              value={approvalFilter}
              onChange={setApprovalFilter}
              style={{ width: 180 }}
            >
              <Option value="all">All Approvals</Option>
              <Option value="Pending Approval">Pending Approval</Option>
              <Option value="Approved">Approved</Option>
              <Option value="Rejected">Rejected</Option>
            </Select>
            <Button icon={<FilterOutlined />}>Advanced Filters</Button>
            <Button icon={<DownloadOutlined />}>Export Report</Button>
          </Space>
        </Card>

        <Card>
          <Table
            columns={columns}
            dataSource={filteredReports}
            rowKey="id"
            loading={isLoading}
            scroll={{ x: 1500 }}
            pagination={{
              current: page,
              pageSize,
              total: filteredReports.length,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} reports`,
              onChange: (nextPage, nextSize) => {
                setPage(nextPage);
                if (nextSize && nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                }
              },
            }}
          />
        </Card>
      </Space>
    </div>
  );
};

export default FinalInspectionReportsPage;
